import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { useContext } from 'react';
import SessionContext from '../../contexts/SessionContext';
import API_URL from '../../constants/environment';
import {
    ReportingComparison,
    OverviewBundleCombinedResponse,
    OverviewBundlePartialResponse,
    OverviewBundleResponse,
    OverviewBundleSection,
    OverviewBundleSectionsOnlyResponse,
    OverviewRankingsResponse,
    ReportingRequest,
} from '../../types/reporting';
import { getOverviewBundleQueryKey } from './OverviewReportingBundleContext';
import { rehydrateOverviewTimeseriesResponse } from '../../utils/reportingBySource';

export interface OverviewBundleInput extends ReportingRequest {
    preset?: string;
    comparison?: ReportingComparison;
    includeComparison?: boolean;
    limit?: number;
    /** Selective `all`-slice sections; omit for full three-section bundle. */
    sections?: OverviewBundleSection[];
    /** When true, timeseries points include marketplace bySource (shared union). */
    timeseriesIncludeBySource?: boolean;
    excludeEvsSections?: OverviewBundleSection[];
    excludeEvsSectionsOnly?: boolean;
}

function normalizeRankings(data: OverviewRankingsResponse): OverviewRankingsResponse {
    return {
        locations: data.locations || [],
        vendors: data.vendors || [],
        oems: data.oems || [],
        insurance: data.insurance || [],
        partTypes: data.partTypes || [],
    };
}

function normalizeTimeseries(
    timeseries: OverviewBundleResponse['timeseries'] | undefined,
): OverviewBundleResponse['timeseries'] | undefined {
    return timeseries ? rehydrateOverviewTimeseriesResponse(timeseries) : undefined;
}

function normalizeBundleResponse(
    data: OverviewBundleResponse | OverviewBundleSectionsOnlyResponse,
): OverviewBundleResponse {
    return {
        ...data,
        timeseries: normalizeTimeseries(data.timeseries),
        rankings: data.rankings ? normalizeRankings(data.rankings) : undefined,
    };
}

function normalizePartialResponse(data: OverviewBundlePartialResponse): OverviewBundlePartialResponse {
    return {
        ...data,
        timeseries: normalizeTimeseries(data.timeseries),
        rankings: data.rankings ? normalizeRankings(data.rankings) : undefined,
    };
}

function normalizeCombinedResponse(data: OverviewBundleCombinedResponse): OverviewBundleCombinedResponse {
    return {
        ...data,
        timeseries: rehydrateOverviewTimeseriesResponse(data.timeseries),
        rankings: normalizeRankings(data.rankings),
        excludeEvsOverlay: {
            ...data.excludeEvsOverlay,
            timeseries: normalizeTimeseries(data.excludeEvsOverlay.timeseries),
            rankings: data.excludeEvsOverlay.rankings
                ? normalizeRankings(data.excludeEvsOverlay.rankings)
                : undefined,
        },
    };
}

export const getOverviewBundle = async (
    sessionId: string,
    input: OverviewBundleInput,
): Promise<OverviewBundleResponse> => {
    const { data } = await axios.post<
        OverviewBundleResponse | OverviewBundleSectionsOnlyResponse
    >(
        `${API_URL}/reporting/overview/bundle`,
        input,
        { headers: { Authorization: sessionId } },
    );

    return normalizeBundleResponse(data);
};

/** Full `all` bundle + excludeEvs overlay in one dual-cube server load. */
export const getOverviewBundleWithOverlay = async (
    sessionId: string,
    input: OverviewBundleInput & { excludeEvsSections: OverviewBundleSection[] },
): Promise<OverviewBundleCombinedResponse> => {
    const { data } = await axios.post<OverviewBundleCombinedResponse>(
        `${API_URL}/reporting/overview/bundle`,
        {
            ...input,
            excludeEvsSections: input.excludeEvsSections,
            // Explicitly omit incremental-only so the API returns combined.
            excludeEvsSectionsOnly: undefined,
        },
        { headers: { Authorization: sessionId } },
    );

    return normalizeCombinedResponse(data);
};

export const getOverviewBundleExcludeEvsSections = async (
    sessionId: string,
    input: OverviewBundleInput & {
        excludeEvsSectionsOnly: true;
        excludeEvsSections: OverviewBundleSection[];
    },
): Promise<OverviewBundlePartialResponse> => {
    const { data } = await axios.post<OverviewBundlePartialResponse>(
        `${API_URL}/reporting/overview/bundle`,
        input,
        { headers: { Authorization: sessionId } },
    );

    return normalizePartialResponse(data);
};

/**
 * Range-keyed merged EV overlay cache (not per section-set).
 * Growing Exclude EVs sections merges into this entry instead of changing the key.
 */
export function getOverviewBundleExcludeEvsQueryKey(
    startDate: string,
    endDate: string,
    granularity: 'day' | 'week' | 'biweekly' | 'month',
    locationId: string | undefined,
    limit: number,
    preset: string | undefined,
    comparison: ReportingComparison | undefined,
) {
    return [
        'reporting',
        'overview',
        'bundle',
        'excludeEvs',
        'merged',
        startDate,
        endDate,
        granularity,
        locationId,
        limit,
        preset,
        comparison,
    ] as const;
}

export function getOverviewBundleCombinedQueryKey(
    startDate: string,
    endDate: string,
    granularity: 'day' | 'week' | 'biweekly' | 'month',
    locationId: string | undefined,
    limit: number,
    preset: string | undefined,
    comparison: ReportingComparison | undefined,
    excludeEvsSections: readonly OverviewBundleSection[],
) {
    return [
        'reporting',
        'overview',
        'bundle',
        'combined',
        startDate,
        endDate,
        granularity,
        locationId,
        limit,
        preset,
        comparison,
        [...excludeEvsSections].sort().join(','),
    ] as const;
}

/** Split a combined response into base + incremental-shaped overlay for cache seeding. */
export function splitCombinedBundleResponse(data: OverviewBundleCombinedResponse): {
    base: OverviewBundleResponse;
    overlay: OverviewBundlePartialResponse;
} {
    const { excludeEvsOverlay, ...baseFields } = data;
    return {
        base: normalizeBundleResponse({
            source: data.source,
            summary: baseFields.summary,
            timeseries: baseFields.timeseries,
            rankings: baseFields.rankings,
        }),
        overlay: normalizePartialResponse({
            source: data.source,
            excludeEvsSectionsOnly: true,
            excludeEvsSections: excludeEvsOverlay.excludeEvsSections,
            summary: excludeEvsOverlay.summary,
            timeseries: excludeEvsOverlay.timeseries,
            rankings: excludeEvsOverlay.rankings,
        }),
    };
}

export const useOverviewBundle = (
    startDate: string,
    endDate: string,
    granularity: 'day' | 'week' | 'biweekly' | 'month' = 'week',
    locationId?: string,
    limit: number = 10,
    preset?: string,
    comparison?: ReportingComparison,
    excludeEvs?: boolean,
    options?: { enabled?: boolean; sections?: OverviewBundleSection[] },
) => {
    const sessionContext = useContext(SessionContext);
    const sections = options?.sections;

    return useQuery({
        queryKey: getOverviewBundleQueryKey(
            startDate,
            endDate,
            granularity,
            locationId,
            limit,
            preset,
            comparison,
        ),
        queryFn: () => getOverviewBundle(sessionContext?.sessionId ?? '', {
            startDate,
            endDate,
            granularity,
            locationId,
            limit,
            preset,
            comparison,
            includeComparison: !!comparison,
            ...(sections?.length ? { sections } : {}),
            ...(excludeEvs ? { excludeEvs: true } : {}),
        }),
        enabled: !!sessionContext?.sessionId && !!startDate && !!endDate && (options?.enabled ?? true),
        staleTime: 1000 * 60 * 5,
        placeholderData: (previousData) => previousData,
    });
};
